// BatchClassifyProgress - E007-S018
// Progress UI for batch tone classification jobs
import React, { useState, useEffect, useCallback } from 'react';
import { toneApi, BatchJobStatus } from '../lib/toneApi';

interface Props {
  jobId: string | null;
  onComplete: () => void;
  onCancel?: () => void;
}

export const BatchClassifyProgress: React.FC<Props> = ({
  jobId,
  onComplete,
  onCancel
}) => {
  const [status, setStatus] = useState<BatchJobStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pollStatus = useCallback(async () => {
    if (!jobId) return;

    try {
      const result = await toneApi.getBatchStatus(jobId);
      setStatus(result);

      if (result.status === 'complete') {
        onComplete();
      } else if (result.status === 'failed') {
        setError(result.error_message || 'Classification failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get status');
    }
  }, [jobId, onComplete]);

  useEffect(() => {
    if (!jobId) return;

    // Initial poll
    pollStatus();

    // Set up polling interval
    const interval = setInterval(pollStatus, 2000);

    return () => clearInterval(interval);
  }, [jobId, pollStatus]);

  if (!jobId) return null;

  const progress = status?.progress;
  const percent = progress?.percent || 0;

  return (
    <div className="bg-white dark:bg-boxdark rounded-lg border border-stroke dark:border-strokedark p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-black dark:text-white flex items-center gap-2">
          <span className="animate-pulse">🔄</span>
          Classifying Creatives
        </h3>
        {onCancel && status?.status === 'processing' && (
          <button
            onClick={onCancel}
            className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          >
            Cancel
          </button>
        )}
      </div>

      {/* Progress Bar */}
      <div className="mb-3">
        <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400 mb-1">
          <span>
            {status?.status === 'queued' && 'Waiting in queue...'}
            {status?.status === 'processing' && 'Processing...'}
            {status?.status === 'complete' && 'Complete!'}
            {status?.status === 'failed' && 'Failed'}
          </span>
          <span>{percent}%</span>
        </div>
        <div className="w-full bg-gray-200 dark:bg-meta-4 rounded-full h-3">
          <div
            className={`h-3 rounded-full transition-all duration-300 ${
              status?.status === 'failed' 
                ? 'bg-red-500' 
                : status?.status === 'complete'
                  ? 'bg-green-500'
                  : 'bg-primary'
            }`}
            style={{ width: `${percent}%` }}
          />
        </div>
      </div>

      {/* Stats */}
      {progress && (
        <div className="grid grid-cols-3 gap-4 text-center">
          <div className="bg-gray-50 dark:bg-meta-4 rounded-lg p-2">
            <p className="text-lg font-bold text-black dark:text-white">{progress.completed}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Completed</p>
          </div>
          <div className="bg-gray-50 dark:bg-meta-4 rounded-lg p-2">
            <p className="text-lg font-bold text-black dark:text-white">{progress.total - progress.completed - progress.failed}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Remaining</p>
          </div>
          <div className="bg-gray-50 dark:bg-meta-4 rounded-lg p-2">
            <p className="text-lg font-bold text-red-500">{progress.failed}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Failed</p>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Success */}
      {status?.status === 'complete' && (
        <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <p className="text-sm text-green-600 dark:text-green-400 flex items-center gap-2">
            <span>✅</span>
            {progress?.completed} creatives classified successfully!
          </p>
        </div>
      )}

      {/* Estimated Time */}
      {status?.status === 'processing' && progress && progress.total > 0 && (
        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400 text-center">
          Estimated time remaining: ~{Math.ceil((progress.total - progress.completed) / 5)} seconds
        </p>
      )}
    </div>
  );
};

// Hook for managing batch classification
export const useBatchClassify = () => {
  const [jobId, setJobId] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startBatch = async (creativeIds?: string[]) => {
    setIsStarting(true);
    setError(null);

    try {
      const job = await toneApi.batchClassify(creativeIds);
      setJobId(job.job_id);
      return job;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start batch');
      throw err;
    } finally {
      setIsStarting(false);
    }
  };

  const reset = () => {
    setJobId(null);
    setError(null);
  };

  return {
    jobId,
    isStarting,
    error,
    startBatch,
    reset
  };
};

export default BatchClassifyProgress;
